// ============================================================
// LinuxShellScripting.jsx
//
// A branded, responsive course content page for the
// "Linux Shell Scripting" lesson.
//
// Author: ProDev Linux Series
// Features: Interactive code blocks, copy-to-clipboard, and
//           responsive layout for junior developers.
// ============================================================

import React, { useState, useEffect } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Fab,
    Zoom,
    Stack
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import TerminalIcon           from "@mui/icons-material/Terminal";
import CodeIcon               from "@mui/icons-material/Code";
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import PlayArrowIcon          from "@mui/icons-material/PlayArrow";
import SettingsIcon           from "@mui/icons-material/Settings";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon               from "@mui/icons-material/Link";
import ExpandMoreIcon         from "@mui/icons-material/ExpandMore";
import ExpandLessIcon         from "@mui/icons-material/ExpandLess";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function ConceptCard({ icon, title, description }) {
    return (
        <Paper elevation={0} sx={{
            p: 2.5, height: "100%", background: BRAND.surface,
            border: `1px solid ${BRAND.border}`, borderRadius: 3,
            transition: "0.25s", "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 20px ${BRAND.goldGlow}` },
        }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}>
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`, borderRadius: 2.5, overflow: "hidden", mb: 1.5 }}>
            <Box onClick={() => setOpen(!open)} sx={{ px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", background: open ? BRAND.goldGlow : BRAND.surface }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>{title}</Typography>
                </Box>
                {open ? <ExpandLessIcon sx={{ color: BRAND.gold }} /> : <ExpandMoreIcon sx={{ color: BRAND.muted }} />}
            </Box>
            <Collapse in={open}>
                <Box sx={{ px: 2.5, py: 2, background: "rgba(0,0,0,0.25)", borderTop: `1px solid ${BRAND.border}` }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

function ResourceLink({ href, label, sub }) {
    return (
        <Box component="a" href={href} target="_blank" rel="noreferrer" sx={{
            display: "flex", alignItems: "center", gap: 2, p: 2, mb: 1.2, textDecoration: "none",
            border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.surface, transition: "0.25s",
            "&:hover": { borderColor: BRAND.borderHover, boxShadow: `0 0 16px ${BRAND.goldGlow}` },
        }}>
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box><Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14 }}>{label}</Typography>
                {sub && <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{sub}</Typography>}</Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function LinuxShellScripting() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Automation · Module 05"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Linux <span>Shell Scripting</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Shell scripting is the art of automating repetitive tasks. By combining Linux commands into executable files, you transform from a manual operator into a systems architect.
                    </Typography>
                </Box>

                {/* WHAT IS SCRIPTING? */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<IntegrationInstructionsIcon />} label="What is a Shell Script?" />
                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.8, fontSize: { xs: 13, md: 15 } }}>
                        A shell script is a plain text file containing a sequence of commands that the <strong style={{ color: BRAND.white }}>Shell</strong> (typically Bash) executes in order.
                        It allows for complex logic, error handling, and automation of everything from server backups to automated software deployments.
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* KEY CONCEPTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="Scripting Fundamentals" />
                    <Grid container spacing={2}>
                        {[
                            { icon: <TerminalIcon />, title: "The Shebang (#!)", description: "The first line tells Linux which interpreter to use. #!/bin/bash is the standard for Bash scripts." },
                            { icon: <SettingsIcon />, title: "Variables", description: "Store data for later use. Define them without spaces: name=\"Hazzan\" and access with $name." },
                            { icon: <PlayArrowIcon />, title: "Execution Bits", description: "Scripts must be granted 'execute' permissions using chmod +x before they can be run as programs." },
                        ].map((card) => (
                            <Grid item xs={12} md={4} key={card.title}><ConceptCard {...card} /></Grid>
                        ))}
                    </Grid>
                </Box>

                {/* TRY IT YOURSELF */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="Creating Your First Script" />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>📜 Step 1: Write the Script</Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2 }}>Use a text editor like <code style={{ color: BRAND.goldBright }}>nano</code> to create a file named <code style={{ color: BRAND.goldBright }}>hello.sh</code>.</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ nano hello.sh", comment: "Open the editor" },
                    ]} />
                    <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, p: 2.5, mb: 3 }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: "rgba(255,255,255,0.35)" }}>#!/bin/bash</Typography>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText }}>echo "Hello, Linux world!"</Typography>
                    </Box>

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>⚙️ Step 2: Make It Executable</Typography>
                    <CodeBlock lines={[
                        { cmd: "$ chmod +x hello.sh", comment: "Grant execution permissions" },
                        { cmd: "$ ./hello.sh", comment: "Run the script from the current directory" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2, mt: 4 }}>🔽 Advanced Script Features</Typography>
                    <ExpandableExample title="Conditionals & User Input" icon={<SettingsIcon />}>
                        <CodeBlock lines={[
                            { cmd: "read name", comment: "Capture user keyboard input" },
                            { cmd: "if [ \"$name\" == \"admin\" ]; then", comment: "Start if-statement" },
                            { cmd: "  echo \"Access Granted\"", comment: "Indented logic" },
                            { cmd: "fi", comment: "End if-statement" },
                        ]} />
                    </ExpandableExample>

                    <ExpandableExample title="Loops & Reusable Functions" icon={<CodeIcon />}>
                        <CodeBlock lines={[
                            { cmd: "for i in {1..5}; do echo $i; done", comment: "One-liner for loop" },
                            { cmd: "myfunc() { echo \"Hi\"; }", comment: "Defining a function" },
                            { cmd: "myfunc", comment: "Calling the function" },
                        ]} />
                    </ExpandableExample>

                    <ExpandableExample title="The Quick Lab" icon={<TerminalIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2 }}>Create and run a date-logger in one go:</Typography>
                        <CodeBlock lines={[
                            { cmd: "$ echo -e '#!/bin/bash\\necho \"Today is $(date)\"' > today.sh", comment: "Write file" },
                            { cmd: "$ chmod +x today.sh", comment: "Grant perms" },
                            { cmd: "$ ./today.sh", comment: "Execute" },
                        ]} />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Module Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Understand the purpose of the #!/bin/bash shebang",
                            "Create and edit script files in the terminal",
                            "Assign execute permissions with chmod",
                            "Utilize variables and capture user input",
                            "Implement basic if/then logic and loops",
                            "Define and call reusable shell functions",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* RESOURCES */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Recommended Resources" />
                    <ResourceLink href="https://linuxcommand.org/lc3_writing_shell_scripts.php" label="LinuxCommand.org" sub="A comprehensive walkthrough for writing your first scripts." />
                    <ResourceLink href="https://tldp.org/LDP/Bash-Beginners-Guide/html/" label="Bash Beginner's Guide" sub="The definitive handbook for Bash shell scripting." />
                    <ResourceLink href="https://explainshell.com/" label="ExplainShell" sub="Paste any command to see a visual breakdown of its arguments." />
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Junior Dev Tip:</strong> Always name your scripts with the <code style={{ color: BRAND.goldBright }}>.sh</code> extension. While Linux doesn't strictly require it, it helps you and your teammates identify scripts at a glance!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
